import { ModelException } from '$shared/exceptions/ModelException';
import { sentry } from '$shared/helpers/logger/sentry';
import { SchemaType } from '$shared/constants/SchemaType';
import globalAlert from '$shared/stores/globalAlertStore';
import type { SchemaMeta } from '$shared/models/SchemaMeta';

class SchemaData {
  private unauthorized = true;

  private schemas: Map<string, SchemaMeta[]> = new Map();
  private schemaObject: Map<string, ObjectType<string>> = new Map();

  public setSchemaList(schemas: ObjectType<SchemaMeta[]>): void {
    this.schemas = new Map(Object.entries(schemas));
  }

  public setSchemaObject(schema: ObjectType<ObjectType<string>>): void {
    this.schemaObject = new Map(Object.entries(schema));
  }

  public getSchemaList(type?: SchemaType): SchemaMeta[] {
    if (!type) {
      const schemas = Array.from(this.schemas.values());
      return schemas.reduce((returnSchemas: SchemaMeta[], currentSchema: SchemaMeta[]) => {
        if (!Array.isArray(currentSchema)) return returnSchemas;
        return returnSchemas.concat(currentSchema);
      }, []);
    }

    return this.get(type);
  }

  public getSchemaLabel(schema: SchemaType, type: string): string {
    return this.getSchemaList(schema).find((item) => item.value === type)?.description || type;
  }

  public getSchema(type: SchemaType): ObjectType<string> {
    if (this.unauthorized) return {};

    try {
      const schemaObject = this.schemaObject.get(type) || {};

      // if (Object.keys(schemaObject).length === 0) {
      //   const err = new Error(`No enum schema available for ${type}`);
      //   globalAlert.show({ message: err?.message }, true);
      // }

      return schemaObject;
    } catch (err: any) {
      const exception = ModelException.from('SchemaData', 'getSchema', { type }, err, {});
      sentry.log(exception);
      globalAlert.show({ message: err?.message || err?.title }, true);
      return {};
    }
  }

  public getSchemaAsCommonList(type: SchemaType): CommonList[] {
    const list = this.get(type);
    return list.map((item: SchemaMeta) => item.toCommonList());
  }

  public getStateAsCommonlist(states?: string[] | null, forLicense = false): CommonList[] {
    const list = this.get(SchemaType.State);
    const State: ObjectType<string> = this.schemaObject.get(SchemaType.State) || {};
    const ignoreStatesForLicense: string[] = [State.AS, State.GU, State.MP, State.PR, State.VI];

    const filteredStateList: SchemaMeta[] = list.filter((item: SchemaMeta) => {
      if (forLicense && ignoreStatesForLicense.includes(item.description)) return false;
      if (Array.isArray(states)) return states?.includes(item.value);
      return true;
    });

    return filteredStateList.map((item: SchemaMeta) => ({
      id: item.value,
      label: `${item.description} (${item.value})`
    }));
  }

  public get Dataset(): ObjectType<string> {
    return this.getSchema(SchemaType.DatasetType) || {};
  }

  public get BoardSource(): ObjectType<string> {
    return this.getSchema(SchemaType.BoardCertificationType) || {};
  }

  public get FacilityInfoType(): ObjectType<string> {
    return this.getSchema(SchemaType.FacilityInfoType) || {};
  }

  public get SourceIssue(): ObjectType<string> {
    return this.getSchema(SchemaType.SourceIssue) || {};
  }

  public get State(): ObjectType<string> {
    return this.getSchema(SchemaType.State) || {};
  }

  public getBoardSourceLabel(type: string): string {
    return (
      this.getSchemaList(SchemaType.BoardCertificationType).find((item) => item.value === type)
        ?.description || type
    );
  }

  public get MonitoringInterval(): ObjectType<string> {
    return this.getSchema(SchemaType.MonitoringInterval) || {};
  }

  private get(type: SchemaType): SchemaMeta[] {
    try {
      if (!(type in SchemaType))
        throw new Error('Trying to fetch schema metadata by using invalid schema type');

      return this.schemas.get(type) || [];
    } catch (err: any) {
      const exception = ModelException.from('SchemaData', 'get', { type }, err, {});
      sentry.log(exception);
      globalAlert.show({ message: err?.message || err?.title }, true);
      return [];
    }
  }

  public onAuthorized(): void {
    this.unauthorized = false;
  }

  public onUnauthorized(): void {
    this.unauthorized = true;
  }

  public reset(): void {
    this.schemas.clear();
    this.schemaObject.clear();
  }
}

export const schemaData = new SchemaData();
